import { z } from 'zod';

import type { components } from '@/types/api';

export type InquiryForward = components['schemas']['InquiryForward'];

export type InquiryForwardTarget = 'new' | 'previous';

/** A label that fails to parse is dropped on its own, so a bad label costs the label and never the card. */
function optionalLabel<Schema extends z.ZodTypeAny>(schema: Schema) {
  return schema.optional().catch(undefined);
}

const titleSchema = z.object({
  cancerFocus: z.string(),
  age: z.number().nullable(),
  diagnosis: z.string(),
});

const expertSchema = z.object({
  id: z.number().int(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  identifiesAsDoctor: z.boolean(),
});

const inquiryRefSchema = z.object({ id: z.number().int(), conversationId: z.string().min(1) });

const inquiryForwardSchema = z.object({
  previousInquiry: inquiryRefSchema.extend({
    title: optionalLabel(titleSchema),
    question: optionalLabel(z.string().nullable()),
  }),
  newInquiry: inquiryRefSchema.extend({
    expert: optionalLabel(expertSchema.nullable()),
  }),
});

/**
 * A message's forward, or null when it has none or its ids are malformed.
 *
 * Attributes are a free-form bag the API passes through from the stored row,
 * so a partial forward is possible and must cost only the card, never the
 * thread that renders it. The ids decide whether there is a card at all; the
 * labels were added later and are absent on older notes, so each one is
 * optional and dropped alone if it does not parse.
 *
 * Kept apart from the thread service, which pulls in the API client, so the
 * pure thread-item builder can use it without loading auth.
 */
export function toInquiryForward(value: unknown): InquiryForward | null {
  const result = inquiryForwardSchema.safeParse(value);

  return result.success ? result.data : null;
}
